import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDarkMode } from '../../context/DarkModeContext';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../context/ToastContext';
import { 
  IoDocumentTextOutline,
  IoEyeOutline,
  IoShareOutline,
  IoTrashOutline,
  IoAddOutline,
  IoCalendarOutline,
  IoCheckmarkCircleOutline,
  IoTimeOutline
} from 'react-icons/io5';
import { usePerformanceReviews } from '../../hooks/usePerformanceReviews';

interface ReviewsTabProps {
  focus: any;
  onPrepareReview: () => void;
}

const ReviewsTab: React.FC<ReviewsTabProps> = ({ focus, onPrepareReview }) => {
  const { darkMode } = useDarkMode();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { reviews, loading, error, refreshReviews, deleteReview } = usePerformanceReviews(focus?.id);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Style classes
  const textColor = darkMode ? 'text-gray-100' : 'text-gray-900';
  const mutedTextColor = darkMode ? 'text-gray-400' : 'text-gray-600';
  const cardBg = darkMode ? 'bg-gray-800' : 'bg-white';
  const borderColor = darkMode ? 'border-gray-700' : 'border-gray-200';
  const hoverBg = darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50';

  const handleDeleteReview = async (reviewId: string, reviewName: string) => {
    if (!confirm(`Are you sure you want to delete "${reviewName}"?`)) return;

    setDeletingId(reviewId);
    const success = await deleteReview(reviewId);
    
    if (success) {
      showToast('Review deleted successfully', 'success');
    } else {
      showToast('Failed to delete review', 'error');
    }
    setDeletingId(null);
  };

  const getReviewStatus = (review: any) => {
    // Determine status based on metadata
    const daysSinceCreation = Math.floor(
      (new Date().getTime() - new Date(review.created_at).getTime()) / (1000 * 60 * 60 * 24)
    );

    if (review.is_pinned) return { label: 'Shared', color: 'text-green-600 dark:text-green-400' };
    if (daysSinceCreation === 0) return { label: 'Draft', color: 'text-yellow-600 dark:text-yellow-400' };
    return { label: 'Final', color: 'text-blue-600 dark:text-blue-400' };
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    const diffTime = Math.abs(today.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`text-center py-12 ${mutedTextColor}`}>
        <p>Error loading reviews: {error}</p>
        <button
          onClick={refreshReviews}
          className="mt-4 text-purple-600 hover:text-purple-700"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <motion.div
      key="reviews"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.2 }}
    >
      <div className="flex justify-between items-start mb-6">
        <div>
          <h3 className={`text-lg font-medium ${textColor}`}>Performance Review Documents</h3>
          <p className={`text-sm ${mutedTextColor} mt-1`}>
            All your prepared review documents in one place
          </p>
        </div>
        <button
          onClick={onPrepareReview}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
        >
          <IoAddOutline className="w-5 h-5" />
          <span>Prepare New Review</span>
        </button>
      </div>

      {reviews.length > 0 ? (
        <div className="space-y-4">
          <AnimatePresence>
            {reviews.map((review, index) => {
              const status = getReviewStatus(review);
              const metrics = review.preparation_data?.keyMetrics || [];
              const achievements = review.preparation_data?.selectedAchievements || [];
              
              return (
                <motion.div
                  key={review.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ delay: index * 0.05 }}
                  className={`p-6 rounded-lg border ${borderColor} ${cardBg} ${hoverBg} transition-colors`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <IoDocumentTextOutline className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                        <h4 className={`text-lg font-medium ${textColor}`}>
                          {review.review_name}
                        </h4>
                        <span className={`text-sm font-medium ${status.color}`}>
                          {status.label}
                        </span>
                      </div>

                      <div className="flex items-center gap-4 text-sm mb-3">
                        <div className="flex items-center gap-1">
                          <IoCalendarOutline className={`w-4 h-4 ${mutedTextColor}`} />
                          <span className={mutedTextColor}>
                            {formatDate(review.created_at)}
                          </span>
                        </div>
                        {review.review_date && (
                          <div className="flex items-center gap-1">
                            <IoTimeOutline className={`w-4 h-4 ${mutedTextColor}`} />
                            <span className={mutedTextColor}>
                              Review Date: {new Date(review.review_date).toLocaleDateString()}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Content Summary */}
                      <div className={`grid grid-cols-3 gap-4 p-3 rounded-lg ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                        <div className="text-center">
                          <div className={`text-2xl font-bold ${textColor}`}>
                            {achievements.length}
                          </div>
                          <div className={`text-xs ${mutedTextColor}`}>Achievements</div>
                        </div>
                        <div className="text-center">
                          <div className={`text-2xl font-bold ${textColor}`}>
                            {metrics.length}
                          </div>
                          <div className={`text-xs ${mutedTextColor}`}>Metrics</div>
                        </div>
                        <div className="text-center">
                          <div className={`text-2xl font-bold ${textColor}`}>
                            {review.context_counts?.talkingPoints || 0}
                          </div>
                          <div className={`text-xs ${mutedTextColor}`}>Talking Points</div>
                        </div>
                      </div>

                      {/* Executive Summary Preview */}
                      {review.preparation_data?.executiveSummary && (
                        <p className={`text-sm ${mutedTextColor} mt-3 line-clamp-2`}>
                          {review.preparation_data.executiveSummary}
                        </p>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 ml-4">
                      <button
                        onClick={() => navigate(`/review/${review.id}`)}
                        className={`p-2 rounded-lg ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'} transition-colors`}
                        title="View Review"
                      >
                        <IoEyeOutline className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => {
                          // TODO: Implement sharing functionality
                          showToast('Sharing feature coming soon!', 'info');
                        }}
                        className={`p-2 rounded-lg ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'} transition-colors`}
                        title="Share Review"
                      >
                        <IoShareOutline className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDeleteReview(review.id, review.review_name)}
                        disabled={deletingId === review.id}
                        className={`p-2 rounded-lg ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'} transition-colors text-red-500 hover:text-red-600 ${
                          deletingId === review.id ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                        title="Delete Review"
                      >
                        {deletingId === review.id ? (
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-500" />
                        ) : (
                          <IoTrashOutline className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      ) : (
        <div className={`text-center py-12 ${cardBg} border ${borderColor} rounded-lg`}>
          <IoDocumentTextOutline className={`w-12 h-12 ${mutedTextColor} mx-auto mb-4`} />
          <p className={`${textColor} mb-2`}>No review documents yet</p>
          <p className={`text-sm ${mutedTextColor} mb-4`}>
            Create your first review document to track your performance discussions
          </p>
          <button
            onClick={onPrepareReview}
            className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <IoAddOutline className="w-5 h-5" />
            <span>Prepare Your First Review</span>
          </button>
        </div>
      )}

      {/* Tips */}
      <div className={`mt-6 p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800`}>
        <h4 className={`font-medium text-blue-800 dark:text-blue-200 mb-2`}>
          Review Document Tips
        </h4>
        <ul className={`text-sm text-blue-700 dark:text-blue-300 space-y-1`}>
          <li>• Create quarterly reviews to track progress throughout the year</li>
          <li>• Use the share feature to send reviews to your manager before meetings</li>
          <li>• Keep draft reviews to iterate on talking points</li>
          <li>• Reference previous reviews to show growth over time</li>
        </ul>
      </div>
    </motion.div>
  );
};

export default ReviewsTab;